import { Separator } from "@/components/ui/separator";
import { observer } from "mobx-react-lite";
import { useGeneralMapStore } from "../stores/GeneralMapStoreContext";
import { CUSTOM_FILTERS } from "@/config/list";
import { Skeleton } from "@/components/ui/skeleton";
import { SelectedFilters } from "./SelectedFilters";
import { Filters } from "./Filters";

import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { PanelLeftOpen, X, Filter } from "lucide-react";
import * as React from "react";
import { cn } from "@/lib/utils";
import { MapRef } from "react-map-gl/mapbox";

const getRefCurrent = (ref: React.Ref<MapRef> | undefined): MapRef | null => {
	if (!ref) return null;
	if (typeof ref === "function") return null;
	return ref.current ?? null;
};

const FilterSkeleton = () => (
	<div className="space-y-4 relative">
		<div className="space-y-2">
			<Skeleton className="h-4 w-32 bg-blue-50" />
			<Skeleton className="h-4 w-20 bg-blue-50" />
		</div>
		{[...Array(3)].map((_, idx) => (
			<div key={idx} className="space-y-3">
				<Skeleton className="h-5 w-40 bg-blue-50" />
				{[...Array(4)].map((_, i) => (
					<div key={i} className="flex items-center gap-2">
						<Skeleton className="h-4 w-4 rounded bg-blue-50" />
						<Skeleton className="h-4 flex-1 bg-blue-50" />
					</div>
				))}
			</div>
		))}
	</div>
);

type FiltersCardProps = {
	open?: boolean;
	onOpenChange?: (open: boolean) => void;
	className?: string;
};

export const FiltersCard = observer(({ open: openProp, onOpenChange, className }: FiltersCardProps) => {
	const generalMapStore = useGeneralMapStore();
	const { loading, mapStore } = generalMapStore;
	const { mapRef, filters } = mapStore;

	const [internalOpen, setInternalOpen] = React.useState(false);
	const open = openProp ?? internalOpen;
	const setOpen = onOpenChange ?? setInternalOpen;

	const selectedItemsCount =
		filters.selectedIds(CUSTOM_FILTERS.structureGroupIds).size +
		filters.selectedIds(CUSTOM_FILTERS.networkType).size +
		filters.selectedIds(CUSTOM_FILTERS.structureTypesIds).size;

	React.useEffect(() => {
		const timeout = setTimeout(() => {
			const map = getRefCurrent(mapRef);
			if (map) map.resize();
		}, 130);
		return () => clearTimeout(timeout);
	}, [open, mapRef]);

	return (
		<>
			{!open && (
				<Button
					variant="secondary"
					size="icon"
					className="md:hidden absolute left-2 top-2 z-30 shadow rounded-full"
					onClick={() => setOpen(true)}
					aria-label="Open filters"
				>
					<PanelLeftOpen className="h-4 w-4" />
				</Button>
			)}

			{open && (
				<div className="fixed inset-0 bg-black/20 z-20 md:hidden" onClick={() => setOpen(false)} aria-hidden />
			)}

			<Collapsible open={open} onOpenChange={setOpen} className={cn("relative z-30", className)}>
				<div
					className={cn(
						"bg-white shadow h-full border-r overflow-scroll",
						"fixed md:static inset-y-0 left-0",
						"transition-[width] duration-20 ease-in-out",
						open ? "w-72 md:w-60 bg-white border-r" : "w-0 bg-transparent border-0",
					)}
					aria-label="Filters sidebar"
				>
					<CollapsibleTrigger asChild>
						<Button
							variant="ghost"
							size="icon"
							className={cn(
								"absolute -right-3 top-4 z-40 w-8 h-8 rounded-full border border-gray-200 hover:border-gray-300 bg-white shadow hover:bg-white",
								"hidden md:inline-flex",
								open ? "-right-3" : "-right-12",
							)}
							aria-label={
								open
									? "Close filters"
									: selectedItemsCount
										? `Open filters, ${selectedItemsCount} active`
										: "Open filters"
							}
						>
							{open ? (
								<X className="text-gray-600 h-3 w-3" />
							) : (
								<Filter className="text-gray-600 h-3 w-3" />
							)}
							{!open && selectedItemsCount > 0 && (
								<span
									className="absolute -top-1 -right-1 h-4 min-w-4 px-1 rounded-full
                             bg-primary text-primary-foreground text-[10px] leading-none
                             font-medium flex items-center justify-center"
									aria-hidden
								>
									{selectedItemsCount}
								</span>
							)}
						</Button>
					</CollapsibleTrigger>

					<div className={cn("pt-3 pb-1.5 sticky top-0 z-10 bg-white", open ? "px-3" : "px-0")}>
						{open ? (
							<div className="text-sm tracking-tight text-center">
								<span className="font-semibold text-gray-700">{mapStore.markersCount}</span>
								<span className="font-medium text-gray-500 ml-1">structures</span>
							</div>
						) : (
							<div className="hidden md:flex items-center justify-center">
								<span className="sr-only">Filters</span>
							</div>
						)}
					</div>

					{open && <Separator />}

					<CollapsibleContent forceMount>
						<div className={cn(open ? "px-3 pb-3 pt-1.5" : "hidden")}>
							{loading ? (
								<FilterSkeleton />
							) : (
								<>
									<SelectedFilters />
									<Filters />
								</>
							)}
						</div>
					</CollapsibleContent>
				</div>
			</Collapsible>
		</>
	);
});
